import * as tf from '@tensorflow/tfjs';
import {KNeighborsRegressor, setBackend} from '@/vendor/scikitjs/dist/es5'

export default class AIAutoCorrect {
    constructor() {
        setBackend(tf)

        this.blockName = 'AIAutoCorrect';
        this.modelFavourNeutrality = null;
        this.modelFavourContext = null;
        this.img_size = 64;
        this.lut_size = 8;

        this.opacity = {
            minValue: 0,
            maxValue: 100,
        };
    }

    async addBlock(blockNum = lutify.blocks.length, data) {
        UI.createAdjustmentBlockTemplate("ai-auto-correct", blockNum);
        let element = UI.getAdjustmentBlock(blockNum);
        this.addExclamationMark($(element));
        UI.removeBlockBlendingModes($(element));
        UI.appendBlockLoadingSpinner($(element))

        await this.loadModel();
        $(element).find(".expand_options").trigger("click", [false]);

        $(element).find(".opacity_slider").on("input", () => this.opacitySliderHandler($(element)));
        $(element).find(".opacity-input").on("change", () => this.opacityInputHandler($(element)));

        $(element)
            .find(".btn-reset-block")
            .on("click", e => {
                e.stopPropagation();
                this.resetBlock($(element));
            });

        $(element)
            .find('[data-control="favor-neutrality"]')
            .on("change", async e => {
                blocks.checkSwitchHandler($(element));

                lutify.getActiveBlock().favourNeutrality = e.target.checked;
                lutify.getActiveBlock().favourContext = !e.target.checked;
                $(element).find('[data-control="favor-neutrality"]').prop('checked', e.target.checked);
                $(element).find('[data-control="favor-context"]').prop('checked', !e.target.checked);
                let LUT = await this.predict();
                this.apply(LUT);
            })

        $(element)
            .find('[data-control="favor-context"]')
            .on("change", async e => {
                blocks.checkSwitchHandler($(element));

                lutify.getActiveBlock().favourContext = e.target.checked;
                lutify.getActiveBlock().favourNeutrality = !e.target.checked;
                $(element).find('[data-control="favor-context"]').prop('checked', e.target.checked);
                $(element).find('[data-control="favor-neutrality"]').prop('checked', !e.target.checked);
                let LUT = await this.predict();
                this.apply(LUT);
            })

        blocks.add({
            name: this.blockName,
            passes: this.addPasses(),
            blockNum,
            blendingMode: 0,
            blendingModeName: "Normal",
            opacity: 100,
            favourContext: true,
            favourNeutrality: false,
        });

        $(element).find('.header-container .switch input').trigger('click');

        UI.removeBlockLoadingSpinner($(UI.getActiveBlock()));

        if (!data.favourNeutrality) {
            let LUT = await this.predict();
            this.apply(LUT);
        }
    }

    addPasses() {
        let pass = gpu.createRenderPass();
        gpu.addPassToComposer(pass, "toMiddle");
        return [pass];
    }

    async loadModel(toolName = 'AI Auto Correct') {
        if (this.modelFavourNeutrality && this.modelFavourContext) return;
        if (vm.$store.state.UI.AIAutoCorrect.isDownloading) {
            this.showDownloadProgress();
            return;
        }

        let storeData = await this.isModelDownloaded();

        if (!storeData) {
            vm.$store.dispatch('UI/setAIAutoCorrectDownloading', true);

            let response = await vm.$store.dispatch('Projects/fetchSystemFiles', {
                block: 'ai-auto-correct'
            });

            this.showDownloadProgress();

            let progress = [];
            let promises = [];

            response.forEach((file, idx) => {
                promises.push(
                    vm.$store.dispatch("Files/fetchFromS3", {
                        url: file.presignedURL,
                        config: {
                            responseType: "blob",
                            onDownloadProgress: progressEvent => {
                                progress[idx] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            }
                        }
                    })
                )
            })

            let interval = setInterval(async () => {
                if (!progress.length) return;
                let totalProgress = progress.reduce((r, i) => (r + i)) / promises.length;

                if (totalProgress >= 100) {
                    clearInterval(interval);
                    vm.$snackbar(`AI model downloaded. You may now use the ${toolName} tool.`, 'success');
                }

                vm.$store.dispatch('UI/setAIAutoCorrectDownloadProgress', totalProgress.toFixed());
            }, 50)

            let files = await Promise.all(promises);

            storeData = {
                favourNeutrality: {
                    model: new File([files[0]], response[0].name),
                    weights: [
                        new File([files[1]], response[1].name),
                        new File([files[2]], response[2].name),
                    ]
                },
                favourContext: {
                    model: new File([files[3]], response[3].name),
                    weights: [
                        new File([files[4]], response[4].name),
                        new File([files[5]], response[5].name),
                    ]
                }
            }

            vm.$store.dispatch('UI/setAIAutoCorrectDownloading', false);
            await window.blocksStore.setItem('AIAutoCorrect', storeData);
        }

        this.modelFavourNeutrality = await tf.loadLayersModel(
            tf.io.browserFiles([
                storeData.favourNeutrality.model,
                storeData.favourNeutrality.weights[0],
                storeData.favourNeutrality.weights[1]
            ])
        );

        this.modelFavourContext = await tf.loadLayersModel(
            tf.io.browserFiles([
                storeData.favourContext.model,
                storeData.favourContext.weights[0],
                storeData.favourContext.weights[1]
            ])
        );
    }

    async predict(imageURL = null, forward = true) {
        let blockNum = blocks.getBlockIndex_byBlockNum(lutify.getActiveBlock().blockNum);
        let passes = blocks.getAllPassesToBlock(blockNum);

        // check the image size for when using larged media sizes
        if (!imageURL) imageURL = lutify.getIDTLMTimage(passes, false); // getting the main canvas image with IDT LMT

        let imageElement = new Image();
        imageElement.src = imageURL;
        await imageElement.decode();

        let tensor = tf.browser.fromPixels(imageElement);

        tensor = tf.cast(tensor, 'float32');
        tensor = this.resize(tensor, this.img_size);
        let image = this.normalize(tensor);

        let image_reshaped = tf.reshape(image, [1, this.img_size, this.img_size, 3]);
        let target = lutify.getActiveBlock().favourContext ?
            await this.modelFavourContext.apply(image_reshaped, {'training': true}) :
            await this.modelFavourNeutrality.apply(image_reshaped, {'training': true});
        let LUT = forward ? await this.img_to_lut(image, tf.squeeze(target)) : await this.img_to_lut(tf.squeeze(target), image);
        let [r, g, b] = this.split_channels(LUT, 3, 1);

        let text = "TITLE Created with fylm.ai AI Auto Correct\n"
        text = text + "LUT_3D_SIZE 8\n\n"
        for (let i = 0; i < this.lut_size * this.lut_size * this.lut_size; i++) {
            text = text + r[i].toFixed(6).toString() + " " + g[i].toFixed(6).toString() + " " + b[i].toFixed(6).toString() + "\n"
        }

        let LUTFile = new File([text], "ai-auto-correct.cube", {type: "text/plain"});
        let loadedLUTs = await luts.load([LUTFile]);
        return loadedLUTs[0];
    }

    apply(LUT) {
        $(UI.getActiveBlock()).find('.btn-block-needs-update').addClass('hide');
        let pass = gpu.createLUTPass(LUT.texture);
        pass.uid = lutify.getActiveBlock().passes[0].uid;
        let index = gpu.getPassIndexById(lutify.getActiveBlock().passes[0].uid);
        gpu.composer.passes[index] = pass;
        lutify.getActiveBlock().passes[0] = pass;
        this.resetOpacity();
        gpu.render();
    }

    resize(input_image, size) {
        let output_image = tf.image.resizeNearestNeighbor(input_image, [size, size]);
        return output_image;
    }

    normalize(input_image) {
        let output_image = tf.sub(tf.div(input_image, 127.5), 1);
        return output_image;
    }

    async img_to_lut(input_image, target_image) {
        let target = tf.add(tf.mul(target_image, 0.5), 0.5)
        let input = tf.add(tf.mul(input_image, 0.5), 0.5)

        let t = tf.sub(target, input);
        let [tR, tG, tB] = this.split_channels(t, 3, 2);

        let [iR, iG, iB] = this.split_channels(input, 3, 2);
        let icolor = tf.stack([iR, iG, iB]).transpose();

        let a = this.lut_size - 1;
        let LUT = [];
        for (let k = 0; k < this.lut_size; k++) {
            for (let j = 0; j < this.lut_size; j++) {
                for (let i = 0; i < this.lut_size; i++) {
                    LUT.push([i / a, j / a, k / a]);
                }
            }
        }

        LUT = tf.stack(LUT)

        let interpolation = new KNeighborsRegressor(40, 'distance');
        await interpolation.fit(icolor, tR)
        let tRp = interpolation.predict(LUT);

        await interpolation.fit(icolor, tG)
        let tGp = interpolation.predict(LUT);

        await interpolation.fit(icolor, tB)
        let tBp = interpolation.predict(LUT);

        let [lutiR, lutiG, lutiB] = this.split_channels(LUT, 3, 1);
        lutiR = tf.add(lutiR, tRp);
        lutiG = tf.add(lutiG, tGp);
        lutiB = tf.add(lutiB, tBp);
        LUT = tf.stack([lutiR, lutiG, lutiB]).transpose();

        LUT = await tf.clipByValue(LUT, 0, 1);
        return LUT;
    }

    split_channels(tensor, num_splits, axis) {
        let [tR, tG, tB] = tf.split(tensor, num_splits, axis);
        tR = tR.dataSync();
        tG = tG.dataSync();
        tB = tB.dataSync();
        return [tR, tG, tB]
    }

    resetBlock($element) {
        if (lutify.getActiveBlock().favourNeutrality) {
            $element.find('[data-control="favor-context"]').trigger('click');
        }

        this.resetOpacity();
    }

    resetOpacity() {
        $(UI.getActiveBlock()).find(".opacity-input").val(100);
        $(UI.getActiveBlock()).find(".opacity-input").trigger("change");
    }

    opacitySliderHandler($element) {
        let opacityValue = $element.find(".opacity_slider").val();
        let calcOpacityValue = opacityValue * 0.01;

        $element.find(".opacity-input").val(opacityValue);

        lutify.getActiveBlock().opacity = opacityValue;
        lutify.getActiveBlock().passes[0].uniforms.opacity.value = calcOpacityValue;

        gpu.render();
    }

    opacityInputHandler($element) {
        let opacityValue = $element.find(".opacity-input").val();
        let opacitySlider = $element.find(".opacity_slider");

        if (opacityValue > this.opacity.maxValue) opacityValue = this.opacity.maxValue;
        if (opacityValue < this.opacity.minValue) opacityValue = this.opacity.minValue;

        opacitySlider.val(opacityValue);
        opacitySlider.trigger("input");
    }

    showDownloadProgress() {
        vm.$snackbar(`Downloading AI model. This might take a while only the first time. `, "success", {
            timeout: -1,
            showAIAutoCorrectDownloadProgress: true,
        });
    }

    async isModelDownloaded() {
        let storeData = await window.blocksStore.getItem('AIAutoCorrect');
        return storeData;
    }

    addExclamationMark($element) {
        let icon = document.createElement('i');
        icon.setAttribute('class', 'btn-block-needs-update material-icons hide');
        icon.setAttribute('data-tippy-content', 'You have changed the position or a value of AI Auto Correct. This might affect the result. Click this icon to re-calculate the result.');
        icon.innerHTML = 'error_outline';
        icon.addEventListener('click', async () => {
            let LUT = await this.predict();
            this.apply(LUT);
        });
        $element.find('.header-container .controls-wrapper').prepend(icon);
    }

    async applyChanges($block, values, blockData) {
        if (blockData.favourNeutrality) {
            lutify.getActiveBlock().favourContext = blockData.favourContext;
            lutify.getActiveBlock().favourNeutrality = blockData.favourNeutrality;
            $block.find('[data-control="favor-context"]').prop('checked', blockData.favourContext);
            $block.find('[data-control="favor-neutrality"]').prop('checked', blockData.favourNeutrality);
            let LUT = await this.predict();
            this.apply(LUT);
        }

        $block.find(".opacity-input").val(parseInt(blockData.opacity));
        $block.find(".opacity-input").trigger("change");
    }
}
